/**
 * useLcdSurvey
 * Owns the LCD survey modal state and orchestrates the two-step submit:
 *   Step 1: call back into useCheckIn → executeCheckIn() (Salesforce CheckIn_VisitPlan).
 *   Step 2: POST /lcd-survey/submit with the visitPlanRecordId returned by step 1.
 *
 * Survey is persisted ONLY if the Salesforce check-in succeeded. If the survey
 * POST itself fails after a successful SF check-in, we surface a retry — the SF
 * record exists, and the next survey POST attempt will dedup on visitPlanRecordId.
 */

import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { lcdSurveyService } from "../services/lcdSurveyService";

export const useLcdSurvey = ({ showError } = {}) => {
  const userCode = useSelector((state) => state?.userDetails?.details?.SH_Code__c);
  const userEmail = useSelector((state) => state?.userDetails?.details?.Email);
  const salesGroupCode = useSelector(
    (state) => state?.userDetails?.details?.Sales_Group_Code__c
  );

  const [isOpen, setIsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // context = { dealerCode, dealerName, clusterCode, clusterName, finyear, quarter, executeCheckIn, onAllSuccess }
  const [context, setContext] = useState(null);

  const checkEligibility = useCallback(
    async ({ dealerCode }) => {
      if (!salesGroupCode || !dealerCode) return { eligible: false };
      return lcdSurveyService.checkEligibility({
        salesgroup: String(salesGroupCode),
        dealerCode,
        userCode: userCode ? String(userCode) : undefined,
      });
    },
    [salesGroupCode, userCode]
  );

  const openSurvey = useCallback((ctx) => {
    setContext(ctx);
    setIsOpen(true);
  }, []);

  /**
   * Modal Submit handler. Runs the Salesforce check-in first, then the survey
   * POST. Returns { success } so the modal can clear local state.
   */
  const submitSurvey = useCallback(
    async (answers) => {
      if (!context) return { success: false };

      setSubmitting(true);

      // Step 1: Salesforce check-in (CheckIn_VisitPlan).
      const sfResult = await context.executeCheckIn();
      if (!sfResult?.success || !sfResult.recordId) {
        setSubmitting(false);
        // executeCheckIn already surfaced the error toast.
        return { success: false };
      }

      // Step 2: persist the survey, joined to the SF record by visitPlanRecordId.
      const payload = {
        visitPlanRecordId: sfResult.recordId,
        userCode: userCode ? String(userCode) : "",
        userEmail: userEmail || undefined,
        salesgroup: salesGroupCode ? String(salesGroupCode) : undefined,
        clusterCode: context.clusterCode,
        clusterName: context.clusterName,
        dealerCode: context.dealerCode ? String(context.dealerCode) : "",
        dealerName: context.dealerName,
        finyear: context.finyear,
        quarter: context.quarter,
        reasonForCompetitionPresence: answers.reasonForCompetitionPresence?.trim(),
        actionPlanForShareGain: answers.actionPlanForShareGain?.trim(),
        updateAgainstPlannedActions: answers.updateAgainstPlannedActions?.trim(),
      };

      const surveyResult = await lcdSurveyService.submitSurvey(payload);
      setSubmitting(false);

      if (!surveyResult.success) {
        // SF check-in already succeeded — keep modal open so user can retry the
        // survey POST. The unique index on visit_plan_record_id will dedup
        // duplicate attempts safely.
        showError && showError(surveyResult.message || "Failed to submit survey");
        return { success: false };
      }

      setIsOpen(false);
      setContext(null);
      // No LCD-specific toast — useCheckIn's finalize step shows the unified
      // "Successfully checked-in for X dealer!" message.
      context.onAllSuccess && context.onAllSuccess();
      return { success: true };
    },
    [context, userCode, userEmail, salesGroupCode, showError]
  );

  return {
    isOpen,
    setIsOpen,
    submitting,
    context,
    checkEligibility,
    openSurvey,
    submitSurvey,
  };
};

export default useLcdSurvey;
